import { GridFields, InfoCard } from 'components';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Chip, MenuOption } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';
import { CountryRiskBadges } from 'modules/client/common/feature';

import { HeadItem, HeadOptionType } from './types';

export interface HeadsInfoCardProps {
  items: HeadItem[];
  editable?: boolean;
  onClickDelete?: (row: HeadItem) => void;
  onCellClick: (row: HeadItem) => void;
  onClickArchive?: (row: HeadItem) => void;
}

export const HeadsInfoCard = ({
  items,
  onCellClick,
  onClickArchive,
  onClickDelete,
  editable,
}: HeadsInfoCardProps) => {
  const { t } = useTranslation();

  const shownOptions: Partial<Record<HeadOptionType, boolean>> = {
    archive: !!onClickArchive,
    dearchive: !!onClickArchive,
    delete: !!onClickDelete,
  };

  const onClickMenuOption = (
    item: HeadItem,
    option: MenuOption<HeadOptionType>
  ) => {
    switch (option.value) {
      case HeadOptionType.delete:
        if (item.id) {
          onClickDelete?.(item);
        }
        break;
      case HeadOptionType.archive:
      case HeadOptionType.dearchive:
        if (item.id) {
          onClickArchive?.(item);
        }
        break;
      default:
        break;
    }
  };

  return (
    <GridFields>
      {items.map((item) => (
        <InfoCard
          fields={[
            {
              label: t('client.legal.companyHeads.columns.position'),
              value: item.position,
            },
            {
              label: t('client.legal.companyHeads.columns.status'),
              value: (
                <Chip
                  size="xs"
                  text={
                    item.archived
                      ? t('common.archivedStatus')
                      : t('common.activeStatus')
                  }
                  variant={item.archived ? 'gray' : 'green'}
                />
              ),
            },
            {
              label: t(
                `client.legal.companyHeads.columns.country.${item.type}`
              ),
              value: item.country?.[item.type],
              valueAdornment: item.countryRisk && (
                <CountryRiskBadges {...item.countryRisk} />
              ),
            },
            {
              label: t('client.legal.companyHeads.columns.expirationDate'),
              value: item.expirationDate,
            },
          ]}
          menuOptions={
            editable
              ? lodash.compact([
                  shownOptions.archive &&
                    !item.archived && {
                      value: HeadOptionType.archive,
                      label: t('common.archive'),
                      Icon: Icon.TimeSleep,
                    },
                  shownOptions.dearchive &&
                    item.archived && {
                      value: HeadOptionType.dearchive,
                      label: t('common.dearchive'),
                      Icon: Icon.RotateRightCheck,
                    },
                  shownOptions.delete && {
                    value: HeadOptionType.delete,
                    label: t('common.delete'),
                    Icon: Icon.Bucket,
                    color: Colors.red,
                  },
                ])
              : undefined
          }
          title={item.name}
          variant={item.archived ? 'gray' : 'default'}
          onClick={() => onCellClick(item)}
          onClickMenuOption={(option) => onClickMenuOption(item, option)}
        />
      ))}
    </GridFields>
  );
};
